import {
  Calendar,
  CalendarDays,
  CheckCircle2,
  Clock,
  ExternalLink,
  Layers,
  ListTodo,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  User,
  UserCheck,
  UserX,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { formatOffsetTime } from '../../lib/job-plan-time';
import { isTerminalStatus } from '../../lib/task-list';
import { trpc } from '../../lib/trpc';
import type { Job, OperatorProfile, Skill } from '../../types/job-plan';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';
import { JOB_STATUS_COLOR_MAP } from './JobCard';
import { JobLocationMap } from './JobLocationMap';
import { JobSlotFinderDialog } from './JobSlotFinderDialog';

export interface JobPreviewSheetProps {
  job: Job | null;
  jobId?: number | null;
  isOpen: boolean;
  onClose: () => void;
  operators?: OperatorProfile[];
  onOpenFullJob?: (jobId: number) => void;
  onJobUpdated?: () => void;
  apiKey?: string;
}

export function JobPreviewSheet({
  job: initialJob,
  jobId: explicitJobId,
  isOpen,
  onClose,
  operators = [],
  onOpenFullJob,
  onJobUpdated,
  apiKey,
}: JobPreviewSheetProps) {
  const activeJobId = explicitJobId ?? initialJob?.id ?? 0;
  const utils = trpc.useUtils();

  const [selectedOpToAssign, setSelectedOpToAssign] = useState<string>('');
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [newDateValue, setNewDateValue] = useState('');
  const [isAdjustingPin, setIsAdjustingPin] = useState(false);
  const [isSlotFinderOpen, setIsSlotFinderOpen] = useState(false);
  const [pinLatitude, setPinLatitude] = useState<number | null>(null);
  const [pinLongitude, setPinLongitude] = useState<number | null>(null);

  // Always query latest job details to ensure fresh contact, coordinates, skills, and assignment
  const { data: queriedJob, isLoading } = trpc.jobPlan.jobs.getById.useQuery(
    { id: activeJobId },
    { enabled: isOpen && activeJobId > 0 }
  );

  const job = queriedJob || initialJob;

  const taskStatusesQuery = trpc.taskStatuses.list.useQuery();
  const taskStatuses = taskStatusesQuery.data?.items ?? [];
  const { data: jobTasks = [] } = trpc.tasks.getAll.useQuery(undefined, {
    enabled: isOpen && activeJobId > 0,
  });
  const filteredTasks = jobTasks.filter((t) => t.jobId === activeJobId);

  const transitionMutation = trpc.jobPlan.jobs.transitionStatus.useMutation({
    onSuccess: () => {
      utils.jobPlan.jobs.getById.invalidate({ id: activeJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.jobPlan.getDaySchedule.invalidate();
      onJobUpdated?.();
    },
  });

  const assignJobMutation = trpc.jobPlan.assignJob.useMutation({
    onSuccess: () => {
      setSelectedOpToAssign('');
      utils.jobPlan.jobs.getById.invalidate({ id: activeJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.jobPlan.getDaySchedule.invalidate();
      onJobUpdated?.();
    },
  });

  const rescheduleMutation = trpc.jobPlan.jobs.reschedule.useMutation({
    onSuccess: () => {
      setIsRescheduling(false);
      utils.jobPlan.jobs.getById.invalidate({ id: activeJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.jobPlan.getDaySchedule.invalidate();
      onJobUpdated?.();
    },
  });

  const updatePinMutation = trpc.jobPlan.jobs.update.useMutation({
    onSuccess: () => {
      setIsAdjustingPin(false);
      utils.jobPlan.jobs.getById.invalidate({ id: activeJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.jobPlan.getDaySchedule.invalidate();
      onJobUpdated?.();
    },
  });

  const handleAssignOperator = () => {
    if (!activeJobId || !selectedOpToAssign || !job) return;
    assignJobMutation.mutate({
      jobId: activeJobId,
      operatorProfileId: parseInt(selectedOpToAssign, 10),
      serviceDate: job.serviceDate,
    });
  };

  const handleReschedule = () => {
    if (!activeJobId || !newDateValue) return;
    rescheduleMutation.mutate({
      jobId: activeJobId,
      newServiceDate: newDateValue,
    });
  };

  const handleSavePin = () => {
    if (!activeJobId || pinLatitude == null || pinLongitude == null) return;
    updatePinMutation.mutate({
      id: activeJobId,
      latitude: pinLatitude,
      longitude: pinLongitude,
      googlePlaceId: null,
    });
  };

  if (!isOpen) return null;

  const statusClass = job
    ? JOB_STATUS_COLOR_MAP[job.status] || JOB_STATUS_COLOR_MAP.unassigned
    : '';
  const durationMin = job ? Math.round(job.estimatedDurationSeconds / 60) : 0;
  const hasWindow =
    job && job.windowStartOffsetSeconds != null && job.windowEndOffsetSeconds != null;
  const windowStr = hasWindow
    ? `${formatOffsetTime(job.windowStartOffsetSeconds!)} - ${formatOffsetTime(
        job.windowEndOffsetSeconds!
      )}`
    : 'Flexible window';

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg lg:max-w-xl p-0 flex flex-col gap-0 overflow-hidden bg-background"
      >
        {/* Header */}
        <SheetHeader className="p-4 border-b border-border/50 bg-card/80 backdrop-blur shrink-0">
          <div className="flex items-start justify-between gap-3 pr-6">
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <SheetTitle className="text-base font-bold truncate">
                  {job?.title || 'Job Preview'}
                </SheetTitle>
                {job && (
                  <Badge
                    variant="outline"
                    className={`text-[11px] capitalize shrink-0 ${statusClass}`}
                  >
                    {job.status.replace('_', ' ')}
                  </Badge>
                )}
                {job && job.priority > 0 && (
                  <Badge variant="secondary" className="text-[10px] bg-amber-500/15 text-amber-600">
                    Priority {job.priority}
                  </Badge>
                )}
              </div>
              <SheetDescription className="text-xs text-muted-foreground truncate">
                {job?.formattedAddress || 'Planning preview'}
              </SheetDescription>
            </div>

            {job && onOpenFullJob && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2 gap-1 text-primary shrink-0"
                onClick={() => onOpenFullJob(job.id)}
              >
                <span>Full Details</span>
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading && !job ? (
            <div className="flex items-center justify-center h-48 gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading job details...</span>
            </div>
          ) : !job ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              Job details not available.
            </div>
          ) : (
            <>
              {/* Service Address & Map Section */}
              <div className="space-y-2 rounded-xl border border-border/50 bg-card p-3.5 shadow-xs">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <span className="text-xs font-semibold text-foreground block">
                        Service Location
                      </span>
                      <span className="text-xs text-muted-foreground mt-0.5 block break-words">
                        {job.formattedAddress}
                      </span>
                      {job.locationNote && (
                        <span className="text-[11px] text-muted-foreground/80 mt-1 italic block">
                          Note: {job.locationNote}
                        </span>
                      )}
                    </div>
                  </div>

                  {!isAdjustingPin ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] text-primary px-2 shrink-0"
                      onClick={() => {
                        setPinLatitude(job.latitude ?? null);
                        setPinLongitude(job.longitude ?? null);
                        setIsAdjustingPin(true);
                      }}
                    >
                      <MapPin className="h-3 w-3 mr-1" />
                      <span>Adjust Pin</span>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] text-muted-foreground px-2 shrink-0"
                      onClick={() => setIsAdjustingPin(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>

                <JobLocationMap
                  latitude={isAdjustingPin ? pinLatitude : (job.latitude ?? null)}
                  longitude={isAdjustingPin ? pinLongitude : (job.longitude ?? null)}
                  heightClassName="h-44"
                  apiKey={apiKey}
                  onPinChange={
                    isAdjustingPin
                      ? (lat, lng) => {
                          setPinLatitude(lat);
                          setPinLongitude(lng);
                        }
                      : undefined
                  }
                />

                {isAdjustingPin && (
                  <div className="flex justify-end pt-1">
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleSavePin}
                      disabled={
                        pinLatitude == null || pinLongitude == null || updatePinMutation.isPending
                      }
                    >
                      {updatePinMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : null}
                      <span>Save Location</span>
                    </Button>
                  </div>
                )}
              </div>

              {/* Timing & Schedule (2-col grid) */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="p-3 rounded-xl border border-border/50 bg-card flex flex-col justify-between gap-1 shadow-xs">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                    <Calendar className="h-3.5 w-3.5 text-primary" />
                    <span>Service Date</span>
                  </div>
                  {!isRescheduling ? (
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-xs font-semibold text-foreground">
                        {job.serviceDate}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 text-[10px] text-primary px-1.5"
                        onClick={() => {
                          setNewDateValue(job.serviceDate);
                          setIsRescheduling(true);
                        }}
                      >
                        Reschedule
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 pt-1">
                      <Input
                        type="date"
                        value={newDateValue}
                        onChange={(e) => setNewDateValue(e.target.value)}
                        className="h-6 text-[11px] px-1.5 flex-1"
                      />
                      <Button
                        size="sm"
                        className="h-6 text-[10px] px-2"
                        onClick={handleReschedule}
                        disabled={!newDateValue || rescheduleMutation.isPending}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>

                <div className="p-3 rounded-xl border border-border/50 bg-card flex flex-col justify-between gap-1 shadow-xs">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                    <Clock className="h-3.5 w-3.5 text-primary" />
                    <span>Duration & Window</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-foreground">
                      {durationMin} mins
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate">{windowStr}</span>
                  </div>
                </div>
              </div>

              {/* Timing Mode Badge & SLA info if not fixed */}
              {job.timingMode === 'range' && (
                <div className="p-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-center justify-between text-xs text-amber-800 dark:text-amber-300">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span>
                      Multi-Day SLA: {job.earliestServiceDate || job.serviceDate} →{' '}
                      {job.deadlineServiceDate || job.serviceDate}
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-amber-500/20 border-amber-500/40"
                  >
                    Flexible Range
                  </Badge>
                </div>
              )}

              {job.timingMode === 'multi_slot' && (
                <div className="p-2.5 rounded-xl border border-indigo-500/30 bg-indigo-500/10 flex items-center justify-between text-xs text-indigo-800 dark:text-indigo-300">
                  <div className="flex items-center gap-1.5">
                    <Layers className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                    <span>
                      Customer Candidate Windows: {job.availabilityWindows?.length || 0} options
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-indigo-500/20 border-indigo-500/40"
                  >
                    Disjunctive Slots
                  </Badge>
                </div>
              )}

              {/* Interactive Slot Capacity Checker Trigger */}
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs gap-1.5 text-primary border-primary/30 bg-primary/5 hover:bg-primary/10 transition-all font-semibold"
                onClick={() => setIsSlotFinderOpen(true)}
              >
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <span>Find Best Slot / Check Availability</span>
              </Button>

              {/* Operator Assignment Card */}
              <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-2.5 shadow-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <UserCheck className="h-4 w-4 text-primary" />
                    <span>Assigned Operator</span>
                  </div>
                  {job.assignedOperator ? (
                    <Badge variant="secondary" className="text-[10px] bg-primary/10 text-primary">
                      Stop #
                      {job.assignedOperator.position != null
                        ? job.assignedOperator.position + 1
                        : 1}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-[10px] text-amber-600 bg-amber-500/10 border-amber-500/20"
                    >
                      Unassigned Pool
                    </Badge>
                  )}
                </div>

                {job.assignedOperator ? (
                  <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-primary/20 bg-primary/5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                        {job.assignedOperator.operatorName?.[0] || 'O'}
                      </div>
                      <span className="text-xs font-semibold text-foreground truncate">
                        {job.assignedOperator.operatorName}
                      </span>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[11px] px-2 text-destructive hover:bg-destructive/10 border-destructive/30 shrink-0"
                      onClick={() =>
                        transitionMutation.mutate({ id: job.id, targetStatus: 'unassigned' })
                      }
                      disabled={transitionMutation.isPending}
                    >
                      <UserX className="h-3 w-3 mr-1" />
                      <span>Unassign</span>
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <select
                      className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-hidden focus:ring-1 focus:ring-ring"
                      value={selectedOpToAssign}
                      onChange={(e) => setSelectedOpToAssign(e.target.value)}
                    >
                      <option value="">Select operator to assign...</option>
                      {operators.map((op) => (
                        <option key={op.id} value={op.id}>
                          {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      className="h-8 text-xs shrink-0 px-3"
                      onClick={handleAssignOperator}
                      disabled={!selectedOpToAssign || assignJobMutation.isPending}
                    >
                      {assignJobMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : null}
                      <span>Assign</span>
                    </Button>
                  </div>
                )}
              </div>

              {/* Required Skills */}
              <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-2 shadow-xs">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <Wrench className="h-3.5 w-3.5 text-primary" />
                  <span>Required Skills</span>
                </div>
                {(job.requiredSkills || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    No specific skill requirements. Any active operator can service this job.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {(job.requiredSkills || []).map((skill: Skill) => (
                      <Badge
                        key={skill.id}
                        variant="secondary"
                        className="text-xs px-2 py-0.5"
                        style={
                          skill.color
                            ? {
                                backgroundColor: `${skill.color}20`,
                                color: skill.color,
                                borderColor: `${skill.color}40`,
                              }
                            : undefined
                        }
                      >
                        {skill.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Customer / Contact Details */}
              {job.contact && (
                <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-2 shadow-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                      <User className="h-3.5 w-3.5 text-primary" />
                      <span>Customer Contact</span>
                    </div>
                  </div>

                  <div className="space-y-1.5 text-xs">
                    <div className="font-semibold text-foreground">
                      {job.contact.name || 'Unnamed Contact'}
                      {job.contact.company && (
                        <span className="text-muted-foreground font-normal ml-1">
                          ({job.contact.company})
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pt-1 text-muted-foreground">
                      {job.contact.phone && (
                        <a
                          href={`tel:${job.contact.phone}`}
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <Phone className="h-3 w-3" />
                          <span>{job.contact.phone}</span>
                        </a>
                      )}
                      {job.contact.email && (
                        <a
                          href={`mailto:${job.contact.email}`}
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <Mail className="h-3 w-3" />
                          <span>{job.contact.email}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Description (if present) */}
              {job.description && (
                <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-1.5 shadow-xs">
                  <span className="text-xs font-semibold text-foreground block">
                    Job Description
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {job.description}
                  </p>
                </div>
              )}

              {/* Checklist / Tasks (if present) */}
              {filteredTasks.length > 0 && (
                <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-2 shadow-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                      <ListTodo className="h-3.5 w-3.5 text-primary" />
                      <span>Checklist Items</span>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {filteredTasks.filter((t) => isTerminalStatus(taskStatuses, t.status)).length}
                      /{filteredTasks.length} Done
                    </Badge>
                  </div>

                  <div className="space-y-1.5">
                    {filteredTasks.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center gap-2 p-2 rounded-lg border border-border/40 bg-muted/20 text-xs"
                      >
                        <CheckCircle2
                          className={`h-3.5 w-3.5 shrink-0 ${
                            isTerminalStatus(taskStatuses, t.status)
                              ? 'text-emerald-600'
                              : 'text-muted-foreground/50'
                          }`}
                        />
                        <span
                          className={`truncate ${
                            isTerminalStatus(taskStatuses, t.status)
                              ? 'line-through text-muted-foreground'
                              : 'text-foreground'
                          }`}
                        >
                          {t.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>

      <JobSlotFinderDialog
        job={job}
        isOpen={isSlotFinderOpen}
        onClose={() => setIsSlotFinderOpen(false)}
        operators={operators}
        onSlotSelected={() => {
          utils.jobPlan.jobs.getById.invalidate({ id: activeJobId });
          utils.jobPlan.getDaySchedule.invalidate();
          onJobUpdated?.();
        }}
      />
    </Sheet>
  );
}
